// atomic.button.v1
//
// Registry shape over the existing UI Button. Plugins declare intent;
// the registry maps it to the legacy `variant` prop so the visual
// behavior stays identical until we redesign the button itself in
// Phase 4.
//
// intent → button:
//   primary-action  → variant="primary"
//   secondary-action → variant="secondary"
//   ghost           → variant="ghost"
//   destructive     → variant="secondary" + critical color overrides

import { type JSX } from "solid-js"
import { Button as LegacyButton, type ButtonProps as LegacyButtonProps } from "../../components/button"
import type { ButtonIntent } from "../types"

export interface RegistryButtonProps
  extends Omit<LegacyButtonProps, "variant"> {
  intent?: ButtonIntent
}

const DESTRUCTIVE_CLASS =
  "!border-transparent !bg-surface-critical-base !text-icon-critical-base !shadow-none hover:!bg-surface-critical-weak"

function intentToVariant(intent: ButtonIntent | undefined): LegacyButtonProps["variant"] {
  switch (intent) {
    case "primary-action":
      return "primary"
    case "secondary-action":
    case "destructive":
      return "secondary"
    case "ghost":
      return "ghost"
    default:
      return "secondary"
  }
}

export function Button(props: RegistryButtonProps): JSX.Element {
  const { intent, class: extraClass, ...rest } = props
  const variant = intentToVariant(intent)
  const composedClass = intent === "destructive"
    ? `${DESTRUCTIVE_CLASS} ${extraClass ?? ""}`.trim()
    : extraClass
  return (
    <LegacyButton
      data-registry-id="atomic.button"
      data-intent={intent ?? "secondary-action"}
      variant={variant}
      class={composedClass}
      {...rest}
    />
  )
}
